import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import Modal, { MODAL_SIZE } from "components/Modal";
import { useAIRoleBuilderContext, useRoleVideoInterviewQuestionsAgent, useToast, useVideoPresentation } from "hooks";
import { AIRoleBuilderProcessing } from "components/Missions/Roles/AIRoleBuilder";
import { AI_ROLE_BUILDER_STATUS, AI_ROLE_BUILDER_STEP } from "constants/index";
import { RoleInterviewAIQuestionsForm } from "components/Missions/Roles";
import { GET_MISSION_ROLE_INTERVIEW } from "graphql/queries";

/**
 * RoleInterviewAIQuestionsModal
 *
 * @param {Object}    missionRole
 * @param {Object}    interview
 * @param {Boolean}   show
 * @param {Function}  onClose
 */
const RoleInterviewAIQuestionsModal = ({ missionRole, interview, show, onClose, ...props }) => {
  const hasTriggeredRef = useRef(false);
  const { toast } = useToast();
  const [step, setStep] = useState(AI_ROLE_BUILDER_STEP.PROCESSING);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  const { handleCreateAgentTask, loading } = useRoleVideoInterviewQuestionsAgent();
  const { handleInterviewQuestionsUpdate, updatingQuestions } = useVideoPresentation();
  const { pendingJob, submitJob, cancelJob, dismissJob } = useAIRoleBuilderContext();

  const handleCompleted = (data) => {
    submitJob(
      data,
      "addRoleVideoInterviewQuestionsAgentTask",
      "Missions.Roles.RoleInterviewAIQuestionsForm.SubmitSuccess"
    );
  };

  const handleError = (error) => {
    cancelJob("Missions.Roles.RoleInterviewAIQuestionsForm.CancelJob");
    dismissJob();
    onClose();
    setStep(null);
    toast.error(error.message);
  };

  const handleClose = () => {
    cancelJob("Missions.Roles.RoleInterviewAIQuestionsForm.CancelJob");
    onClose();
  };

  const handleSelect = useCallback((indexes) => {
    const unique = [...new Set(indexes)];
    setSelectedQuestions((prev) => {
      const isSame = prev.length === unique.length && prev.every((id) => unique.includes(id));
      return isSame ? prev : unique;
    });
  }, []);

  const handleSubmit = () => {
    if (selectedQuestions.length === 0) {
      toast.error(<FormattedMessage id="Missions.Roles.RoleInterviewAIQuestionsForm.NoSelectionError" />);
      return;
    }

    const questionsObjects = selectedQuestions.map((index) => questions[index]);

    handleInterviewQuestionsUpdate({
      interviewId: interview?.uuid,
      questions: questionsObjects,
      onCompleted: () => {
        dismissJob();
        onClose();
      },
      missionRoleId: missionRole?.id,
      refetchQueries: [GET_MISSION_ROLE_INTERVIEW],
    });
  };

  useEffect(() => {
    if (loading) return setStep(AI_ROLE_BUILDER_STEP.PROCESSING);

    if (pendingJob) {
      const status = pendingJob.status;

      if (status === AI_ROLE_BUILDER_STATUS.COMPLETED) {
        setStep(AI_ROLE_BUILDER_STEP.SUCCESS);
        setQuestions(pendingJob.results.missionRole.videoInterviewQuestions);
      } else {
        setStep(AI_ROLE_BUILDER_STEP.PROCESSING);
      }
    }
  }, [pendingJob, loading]);

  useEffect(() => {
    if (show && !hasTriggeredRef.current && missionRole?.description) {
      handleCreateAgentTask({
        jobDescription: missionRole.description,
        onCompleted: handleCompleted,
        onError: handleError,
      });
      hasTriggeredRef.current = true;
    }

    if (!show) {
      hasTriggeredRef.current = false;
    }
  }, [show, missionRole?.description, handleCreateAgentTask]);

  return (
    <Modal
      show={show}
      title={<FormattedMessage id="Missions.Roles.RoleInterviewAIQuestionsForm.Title" />}
      size={MODAL_SIZE.large}
      onClose={handleClose}
      showSave={true}
      showFooter={false}
      canSubmit={selectedQuestions.length > 0 || updatingQuestions}
      onSubmit={handleSubmit}
      primaryButtonMessageId="Global.Add"
      {...props}
    >
      {step === AI_ROLE_BUILDER_STEP.PROCESSING && (
        <AIRoleBuilderProcessing
          titleId="Missions.Roles.RoleInterviewAIQuestionsForm.ProcessingTitle"
          descriptionId="Missions.Roles.RoleInterviewAIQuestionsForm.ProcessingDescription"
          job={pendingJob}
        />
      )}
      {step === AI_ROLE_BUILDER_STEP.SUCCESS && (
        <RoleInterviewAIQuestionsForm questions={questions} onSelect={handleSelect} clearable={false} selectable />
      )}
    </Modal>
  );
};

RoleInterviewAIQuestionsModal.propTypes = {
  missionRole: PropTypes.object.isRequired,
  interview: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RoleInterviewAIQuestionsModal;
